import React, { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Upload, Link as LinkIcon, X, FileText, Video, Image, Music,
  File, Trash2, Download, ExternalLink, Youtube, Loader2, Globe, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_ICONS = {
  pdf: <FileText className="h-5 w-5 text-red-500" />,
  document: <FileText className="h-5 w-5 text-blue-500" />,
  spreadsheet: <FileText className="h-5 w-5 text-green-500" />,
  presentation: <FileText className="h-5 w-5 text-orange-500" />,
  video: <Video className="h-5 w-5 text-purple-500" />,
  image: <Image className="h-5 w-5 text-pink-500" />,
  audio: <Music className="h-5 w-5 text-amber-500" />,
  youtube: <Youtube className="h-5 w-5 text-red-600" />,
  vimeo: <Video className="h-5 w-5 text-blue-500" />,
  link: <Globe className="h-5 w-5 text-teal-500" />,
  other: <File className="h-5 w-5 text-slate-500" />,
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1048576).toFixed(1)} Mo`;
};

const ModuleMediaManager = ({ open, onOpenChange, courseId, moduleId, moduleName, mediaList, onMediaChange }) => {
  const [tab, setTab] = useState('list');
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let count = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_type', 'file');
        formData.append('title', file.name);
        const res = await fetch(`${API}/courses/${courseId}/modules/${moduleId}/media`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          count++;
        } else {
          const err = await res.json();
          toast.error(`Erreur: ${file.name} - ${err.detail}`);
        }
      } catch (e) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} fichier(s) ajouté(s)`);
      onMediaChange();
    }
    setUploading(false);
    setTab('list');
  }, [courseId, moduleId, onMediaChange]);

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('media_type', 'url');
      formData.append('url', urlInput.trim());
      formData.append('title', urlTitle.trim() || urlInput.trim());
      const res = await fetch(`${API}/courses/${courseId}/modules/${moduleId}/media`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        toast.success('Lien ajouté');
        setUrlInput('');
        setUrlTitle('');
        onMediaChange();
        setTab('list');
      } else {
        const err = await res.json();
        toast.error(err.detail);
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
    setUploading(false);
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules/${moduleId}/media/${mediaId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Média supprimé');
        onMediaChange();
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const getIcon = (media) => {
    if (media.type === 'url') return FILE_ICONS[media.url_type] || FILE_ICONS.link;
    return FILE_ICONS[media.file_category] || FILE_ICONS.other;
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="media-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <File className="h-5 w-5 text-teal-500" />
            Médias — {moduleName}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <button
            className={`flex-1 text-sm py-2 px-3 rounded-md transition-colors ${tab === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm font-medium' : 'text-slate-500'}`}
            onClick={() => setTab('list')}
            data-testid="media-tab-list"
          >
            Médias ({mediaList.length})
          </button>
          <button
            className={`flex-1 text-sm py-2 px-3 rounded-md transition-colors ${tab === 'upload' ? 'bg-white dark:bg-slate-700 shadow-sm font-medium' : 'text-slate-500'}`}
            onClick={() => setTab('upload')}
            data-testid="media-tab-upload"
          >
            <Upload className="h-3.5 w-3.5 inline mr-1" />Fichiers
          </button>
          <button
            className={`flex-1 text-sm py-2 px-3 rounded-md transition-colors ${tab === 'url' ? 'bg-white dark:bg-slate-700 shadow-sm font-medium' : 'text-slate-500'}`}
            onClick={() => setTab('url')}
            data-testid="media-tab-url"
          >
            <LinkIcon className="h-3.5 w-3.5 inline mr-1" />Lien URL
          </button>
        </div>

        {/* Tab Content */}
        {tab === 'list' && (
          <ScrollArea className="max-h-[50vh]">
            {mediaList.length === 0 ? (
              <div className="py-12 text-center" data-testid="media-empty">
                <File className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Aucun média attaché à ce module</p>
                <p className="text-slate-400 text-xs mt-1">Ajoutez des fichiers ou liens URL</p>
              </div>
            ) : (
              <div className="space-y-2">
                {mediaList.map((media) => (
                  <div
                    key={media.media_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:border-teal-200 transition-colors"
                    data-testid={`media-item-${media.media_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="shrink-0">{getIcon(media)}</div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{media.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[10px] py-0">
                            {media.type === 'url' ? media.url_type : media.file_category}
                          </Badge>
                          {media.size && <span className="text-xs text-slate-400">{formatSize(media.size)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {media.type === 'url' ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                          <a href={media.url} target="_blank" rel="noopener noreferrer" data-testid={`media-open-${media.media_id}`}>
                            <ExternalLink className="h-4 w-4 text-slate-400" />
                          </a>
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                          <a href={`${API}/media/${media.media_id}/download`} data-testid={`media-download-${media.media_id}`}>
                            <Download className="h-4 w-4 text-slate-400" />
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-400 hover:text-red-600"
                        onClick={() => handleDelete(media.media_id)}
                        data-testid={`media-delete-${media.media_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        )}

        {tab === 'upload' && (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                dragActive ? 'border-teal-500 bg-teal-50' : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              data-testid="media-drop-zone"
            >
              {uploading ? (
                <Loader2 className="h-10 w-10 text-teal-500 mx-auto animate-spin" />
              ) : (
                <Upload className="h-10 w-10 text-slate-300 mx-auto" />
              )}
              <p className="text-sm font-medium text-slate-600 mt-3">
                {uploading ? 'Envoi en cours...' : 'Glissez-déposez vos fichiers ici'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                PDF, Word, Excel, PowerPoint, Images, Vidéos, Audio — Max 100 Mo
              </p>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
                accept="*/*"
                data-testid="media-file-input"
              />
            </div>
          </div>
        )}

        {tab === 'url' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">URL du lien</label>
              <Input
                placeholder="https://www.youtube.com/watch?v=... ou tout autre lien"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                data-testid="media-url-input"
              />
              <p className="text-xs text-slate-400 mt-1">YouTube, Vimeo, Google Drive, ou tout lien externe</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Titre (optionnel)</label>
              <Input
                placeholder="Titre du lien"
                value={urlTitle}
                onChange={(e) => setUrlTitle(e.target.value)}
                data-testid="media-url-title"
              />
            </div>
            <Button
              className="w-full bg-teal-500 hover:bg-teal-600 text-white"
              onClick={handleAddUrl}
              disabled={!urlInput.trim() || uploading}
              data-testid="media-add-url-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Ajouter le lien
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModuleMediaManager;
